import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, Facebook, MessageCircle, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { useBrands } from "@/data/hooks";
import { useLanguage } from "@/i18n/LanguageContext";
import { NandaSaMark } from "@/components/BrandMarks";

const NewsletterInline = () => {
 const { locale } = useLanguage();
 const l = (fr, en, pt) => locale === "en" ? en : locale === "pt" ? pt : fr;
 const [email, setEmail] = useState("");
 const onSubmit = (e) => {
 e.preventDefault;
 if (!email || !email.includes("@")) {
 toast.error(l("Email invalide", "Invalid email", "Email inválido"));
 return;
 }
 toast.success(l("Merci. Vous êtes inscrit·e.", "Thank you. You are subscribed.", "Obrigado. Está inscrito."));
 setEmail("");
 };
 return (
 <form onSubmit={onSubmit} className="flex items-end gap-3 border-b border-nanda-ink/30 focus-within:border-nanda-ink transition-colors" data-testid="footer-newsletter">
 <input
 type="email"
 value={email}
 onChange={(e) => setEmail(e.target.value)}
 placeholder={l("votre@email.com", "your@email.com", "o.seu@email.com")}
 className="flex-1 min-w-0 bg-transparent py-3 outline-none text-nanda-ink placeholder:text-nanda-muted text-sm"
 data-testid="footer-newsletter-input"
 />
 <button type="submit" className="label-overline text-nanda-ink hover:text-nanda-terracotta py-3 whitespace-nowrap transition-colors duration-300" data-testid="footer-newsletter-submit">
 {l("S'abonner", "Subscribe", "Subscrever")} →
 </button>
 </form>
 );
};

export default function Footer() {
 const year = new Date().getFullYear();
 const BRANDS = useBrands();
 const { locale } = useLanguage();
 const l = (fr, en, pt) => locale === "en" ? en : locale === "pt" ? pt : fr;
 return (
 <footer data-testid="site-footer" className="border-t border-nanda-line bg-nanda-bg">
 {/* Inspirational closing line */}
 <div className="border-b border-nanda-line">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28">
 <div className="label-overline text-nanda-muted mb-6">{l("Une dernière chose", "One last thing", "Uma última coisa")}</div>
 <p className="font-serif text-3xl md:text-5xl lg:text-6xl text-balance max-w-4xl leading-[1.1]">
 {l("Demain commence ici, en Afrique de l'Ouest.", "Tomorrow starts here, in West Africa.", "O amanhã começa aqui, na África Ocidental.")}
 </p>
 </div>
 </div>

 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-16 md:py-20 grid grid-cols-2 md:grid-cols-2 lg:grid-cols-12 gap-10">
 <div className="col-span-2 md:col-span-2 lg:col-span-4">
 <div className="flex items-center gap-4">
 <NandaSaMark size={36} className="text-nanda-ink" />
 <div className="font-serif text-3xl md:text-4xl text-nanda-ink leading-none">NANDA SÁ</div>
 </div>
 <div className="label-overline text-nanda-muted mt-4">{l("Guinée-Bissau · Depuis 2023", "Guinea-Bissau · Since 2023", "Guiné-Bissau · Desde 2023")}</div>
 <p className="mt-8 max-w-md text-nanda-inkSoft text-sm md:text-[15px] leading-relaxed">
 {l(
 "Une maison ouest-africaine née entre la Guinée-Bissau. Énergie, épicerie fine, cosmétiques, formation, art de vivre.",
 "A West African house born between Guinea-Bissau. Energy, fine foods, cosmetics, training, lifestyle.",
 "Uma casa oeste-africana nascida entre a Guiné-Bissau. Energia, mercearia fina, cosméticos, formação, arte de viver."
 )}
 </p>
 <div className="mt-8 flex items-center gap-5">
 <a
 href="https://www.instagram.com/nandasa_africa?igsh=MTVnY3VvY2pkYzM1Zw%3D%3D&utm_source=qr"
 target="_blank"
 rel="noopener noreferrer"
 aria-label="Instagram NANDA SÁ"
 className="text-nanda-inkSoft hover:text-nanda-terracotta transition-colors"
 data-testid="footer-instagram"
 >
 <Instagram size={18} strokeWidth={1.5} />
 </a>
 <a
 href="https://www.linkedin.com/company/nanda-s%C3%A1/"
 target="_blank"
 rel="noopener noreferrer"
 aria-label="LinkedIn NANDA SÁ"
 className="text-nanda-inkSoft hover:text-nanda-terracotta transition-colors"
 data-testid="footer-linkedin"
 >
 <Linkedin size={18} strokeWidth={1.5} />
 </a>
 <a
 href="https://www.facebook.com/share/1DQsYahN4c/?mibextid=wwXIfr"
 target="_blank"
 rel="noopener noreferrer"
 aria-label="Facebook NANDA SÁ"
 className="text-nanda-inkSoft hover:text-nanda-terracotta transition-colors"
 data-testid="footer-facebook"
 >
 <Facebook size={18} strokeWidth={1.5} />
 </a>
 <a
 href="https://wa.me/221710090410"
 target="_blank"
 rel="noopener noreferrer"
 aria-label="WhatsApp NANDA SÁ"
 className="text-nanda-inkSoft hover:text-nanda-terracotta transition-colors"
 data-testid="footer-whatsapp"
 >
 <MessageCircle size={18} strokeWidth={1.5} />
 </a>
 </div>
 </div>

 <div className="col-span-1 md:col-span-1 lg:col-span-2">
 <div className="label-overline text-nanda-muted mb-5">{l("Navigation", "Navigation", "Navegação")}</div>
 <ul className="space-y-3 text-sm">
 <li><Link className="text-nanda-ink hover:text-nanda-terracotta transition-colors" to="/maison" data-testid="footer-maison">{l("La Maison", "The House", "A Casa")}</Link></li>
 <li><Link className="text-nanda-ink hover:text-nanda-terracotta transition-colors" to="/marques" data-testid="footer-brands">{l("Marques", "Brands", "Marcas")}</Link></li>
 <li><Link className="text-nanda-ink hover:text-nanda-terracotta transition-colors" to="/secteurs" data-testid="footer-sectors">{l("Secteurs", "Sectors", "Sectores")}</Link></li>
 <li><Link className="text-nanda-ink hover:text-nanda-terracotta transition-colors" to="/tresors">{l("Trésors", "Treasures", "Tesouros")}</Link></li>
 <li><Link className="text-nanda-ink hover:text-nanda-terracotta transition-colors" to="/carnet">{l("Carnet", "Journal", "Caderno")}</Link></li>
 <li><Link className="text-nanda-ink hover:text-nanda-terracotta transition-colors" to="/actualites">{l("Actualités", "News", "Actualidades")}</Link></li>
 <li><Link className="text-nanda-ink hover:text-nanda-terracotta transition-colors" to="/opportunites" data-testid="footer-opportunites">{l("Opportunités", "Opportunities", "Oportunidades")}</Link></li>
 <li><Link className="text-nanda-ink hover:text-nanda-terracotta transition-colors" to="/produits-consommables" data-testid="footer-produits-consommables">{l("Produits & consommables pro", "Professional supplies", "Produtos e consumíveis")}</Link></li>
 <li><Link className="text-nanda-ink hover:text-nanda-terracotta transition-colors" to="/engagement-odd" data-testid="footer-engagement-odd">{l("Engagement ODD", "SDG commitment", "Compromisso ODS")}</Link></li>
 <li><Link className="text-nanda-ink hover:text-nanda-terracotta transition-colors" to="/mediatheque" data-testid="footer-mediatheque">{l("Médiathèque", "Media library", "Mediateca")}</Link></li>
 <li><Link className="text-nanda-ink hover:text-nanda-terracotta transition-colors" to="/impact">{l("Impact", "Impact", "Impacto")}</Link></li>
 <li><Link className="text-nanda-ink hover:text-nanda-terracotta transition-colors" to="/partenaires">{l("Partenaires", "Partners", "Parceiros")}</Link></li>
 <li><Link className="text-nanda-ink hover:text-nanda-terracotta transition-colors" to="/rejoindre">{l("Nous rejoindre", "Join us", "Junte-se a nós")}</Link></li>
 </ul>
 </div>

 <div className="col-span-1 md:col-span-1 lg:col-span-2">
 <div className="label-overline text-nanda-muted mb-5">{l("Nos Maisons", "Our Houses", "As nossas Casas")}</div>
 <ul className="space-y-3 text-sm">
 {BRANDS.map((b) => (
 <li key={b.slug}><Link className="text-nanda-ink hover:text-nanda-terracotta transition-colors" to={`/marques/${b.slug}`} data-testid={`footer-brand-${b.slug}`}>{b.name}</Link></li>
 ))}
 </ul>
 </div>

 <div className="col-span-2 md:col-span-1 lg:col-span-2">
 <div className="label-overline text-nanda-muted mb-5">{l("Contact", "Contact", "Contacto")}</div>
 <div className="space-y-6 text-sm">
 <div>
 <div className="label-overline text-nanda-muted text-[10px] mb-2">{l("Implantations", "Locations", "Implantações")}</div>
 <ul className="space-y-1 text-nanda-inkSoft">
 <li>Canchungo <span className="text-nanda-muted">· Guinée-Bissau</span></li>
 <li><span className="text-nanda-muted">· Guinée-Bissau</span></li>
 </ul>
 </div>
 <div>
 <div className="label-overline text-nanda-muted text-[10px] mb-2">E-mail</div>
 <a href="mailto:hello@nanda-sa.com" className="text-nanda-ink hover:text-nanda-terracotta transition-colors" data-testid="footer-email-link">hello@nanda-sa.com</a>
 </div>
 <div>
 <div className="label-overline text-nanda-muted text-[10px] mb-2">{l("Téléphone & WhatsApp", "Phone & WhatsApp", "Telefone e WhatsApp")}</div>
 <ul className="space-y-1 text-nanda-inkSoft">
 <li><span className="text-nanda-muted">Guinée-Bissau : </span><a href="https://wa.me/245956828351" target="_blank" rel="noopener noreferrer" className="text-nanda-ink hover:text-nanda-terracotta transition-colors whitespace-nowrap" data-testid="footer-phone-gw">+245 956 828 351</a></li>
 <li><span className="text-nanda-muted">Guinée-Bissau : </span><a href="https://wa.me/221710090410" target="_blank" rel="noopener noreferrer" className="text-nanda-ink hover:text-nanda-terracotta transition-colors whitespace-nowrap" data-testid="footer-phone-sn">+221 71 009 04 10</a></li>
 </ul>
 </div>
 </div>
 </div>

 <div className="col-span-2 md:col-span-1 lg:col-span-2">
 <div className="label-overline text-nanda-muted mb-5">{l("Newsletter", "Newsletter", "Newsletter")}</div>
 <p className="text-nanda-inkSoft text-sm leading-relaxed mb-4">{l("Recevez nos lettres trimestrielles.", "Receive our quarterly letters.", "Receba as nossas cartas trimestrais.")}</p>
 <NewsletterInline />
 </div>
 </div>

 <div className="border-t border-nanda-line">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 label-overline text-nanda-muted">
 <div className="flex flex-wrap items-center gap-5">
 <span>© {year} NANDA SÁ</span>
 <Link to="/legal/mentions" className="hover:text-nanda-ink transition-colors" data-testid="footer-legal-mentions">{l("Mentions légales", "Legal notice", "Menções legais")}</Link>
 <Link to="/legal/confidentialite" className="hover:text-nanda-ink transition-colors" data-testid="footer-legal-privacy">{l("Politique de confidentialité", "Privacy policy", "Política de privacidade")}</Link>
 <Link to="/legal/cookies" className="hover:text-nanda-ink transition-colors">Cookies</Link>
 </div>
 <span>GW · SN</span>
 </div>
 </div>
 </footer>
 );
}
